import cookies from '../node_modules/@buzzfeed/bf-utils/lib/cookies/index.js';
import { getDataFromLayers } from './utils.js';
import commonClientMetadata from './layers/common-client-metadata/index.js';
import commonClientMetadataEvent from './layers/common-client-metadata/event.js';
import commonClientMetadataAmpAdjacent from './layers/common-client-metadata/amp-adjacent.js';
import commonWebClientMetadata from './layers/common-web-client-metadata/index.js';
import universalMetadata from './layers/universal-metadata/index.js';
import './layers/index.js';
import { createClientBroadcaster } from './client-broadcaster.js';
import { isEnhancedWebviewFeatureSupported } from '../node_modules/@buzzfeed/bf-utils/lib/webview/enhanced.js';
import '../node_modules/@buzzfeed/bf-utils/lib/webview/index.js';

/**
 * Extracts the smoke test ID from the url if present
 * and adds the is_bot property
 * @return {Object} - The smoke test metadata
 */
function smokeTestMetadata() {
  const isBot = cookies.get('is_bot') === 'true';
  const isSmokeTest = window.location.search.includes('e2e_test');
  if (isSmokeTest) {
    try {
      // Use URLSearchParams which is supported on the browsers we test (fails gracefully otherwise)
      const params = new URLSearchParams(window.location.search);
      return {
        e2e_test: params.get('e2e_test'),
        is_bot: true
      };
    } catch {}
  }
  if (isBot) {
    return {
      is_bot: true
    };
  }
  return {};
}

/**
 * This method creates an event broadcaster.
 * @param  {Object} options.amp_options - Optional amp configuration.
 * @param  {string} options.env- 'dev', 'stage', and 'prod'
 * @param  {string} options.source - Examples: `web_bf`, `web_news`, `web_tasty` and other established source values used for event routing
 * @return {function} - An event creator Function
 */
const createClientEvent = _ref => {
  let {
    amp_options = {},
    env,
    source
  } = _ref;
  /**
   * Can't create a client event when not run on the client side! When executing on the server,
   * return a no-op function.
   */
  if (typeof window === 'undefined') {
    return () => {};
  }
  const sendClientEvent = createClientBroadcaster({
    env
  });

  /**
   * A wrapper for a client event broadcaster function. First argument is an option object
   * configuring the type of event. Additional arguments are for providing layer data.
   * @param {Object} options - Event broadcaster options
   * @param {boolean} [options.flush=false] - Send the event immediately(true) or add to event queue (false)
   * @param {Array} [options.required_layers=[]] - Optional list of required layers for the event being configured.
   * @param {number} [options.sample_rate=1] - Rate at which to sample event requests (between 0 and 1)
   * @param {string} options.type - Event type used for event routing. Examples: 'web_pageview', 'web_impression'
   * @param  {...(Object|function)} layers
   */
  return async function (_ref2) {
    let {
      flush = false,
      required_layers = [],
      sample_rate = 1,
      type
    } = _ref2;
    for (var _len = arguments.length, layers = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      layers[_key - 1] = arguments[_key];
    }
    /**
     * Get data from user inputted layers...
     */
    const {
      /**
       * Extract fields required for processing common/universal layers:
       */
      page_edition,
      /**
       * The rest of the fields will be used as data when evaluating the `required_layers` list.
       */
      ...layer_data
    } = await getDataFromLayers({
      context: {
        env
      },
      layers
    });

    // frameElement will return value if it is from within iframe on same domain.  In that case,
    //  use that as the window to synchronize values
    const win = window.frameElement ? window.parent : window;

    /**
     * check if currently in "enhanced" webview (https://buzzfeed.atlassian.net/wiki/spaces/TECH/pages/3468132356),
     * and that it supports app CET integration.  If so use app version of the type.  An individual event call can also
     * always overwrite the value by providing it in the body
     */
    const shouldUseAppCET = isEnhancedWebviewFeatureSupported({
      feature: 'cet',
      version: 1
    });
    // will need to update random user uuid as provided by app if not at least version 2
    const shouldUpdateAppRandomUserUUID = !isEnhancedWebviewFeatureSupported({
      feature: 'cet',
      version: 2
    });
    const defaultType = shouldUseAppCET ? type.replace('web_', 'app_') : type;
    if (shouldUpdateAppRandomUserUUID) {
      layers.forEach(layer => {
        if (layer?.random_user_uuid) {
          layer.random_user_uuid = layer.random_user_uuid.toLowerCase();
        }
      });
    }
    const event_layers = [
    /**
     * Universal and common layers included in ALL events:
     *
     * NOTE:
     * Layer order is intentional and purposefully not alphabetized. This ordering helps display
     * a useful request preview in devtools which helps debugging and QA'ing the requests.
     */
    universalMetadata({
      source,
      type: defaultType
    }), commonClientMetadataEvent({
      url: win.document.URL
    }), commonClientMetadata({
      // Used to create a unique key for the returned memoized function
      url: win.document.URL
    }), commonClientMetadataAmpAdjacent(amp_options), commonWebClientMetadata({
      page_edition
    }),
    /**
     * Include user inputted layers:
     */
    ...layers,
    /**
     * If a `required_layers` list was provided, process the layers using the data from the
     * `layer_data` object. It's possible required_layers will output the same fields as the user
     * inputted layers but will overwrite those values with additional cleanup and/or validation.
     */
    ...required_layers.map(layer => layer(layer_data)),
    /**
     * Include smoketest metadata in the event data if this is an end to end test
     * (`&e2e_test=[timestamp]`).
     */
    smokeTestMetadata()];

    /**
     * Finally, get a data object from all combined layers. This object is the data sent in the
     * event request body.
     */
    const event_data = await getDataFromLayers({
      context: {
        env
      },
      layers: event_layers
    });
    if (window.location.search.includes('e2e_test') || Math.random() <= sample_rate) {
      await sendClientEvent(event_data, {
        flush
      });
    }
  };
};

export { createClientEvent };
